import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  doc,
  deleteDoc,
  query,
  where
} from "firebase/firestore";
import { db } from "../firebase/config";
import EmojiPicker from "emoji-picker-react";
import "./TasksTab.css";
import Emoji from "./Emoji";

const TASK_CATEGORIES = [
  "Навчання",
  "Вільний час",
  "Рух",
  "Пригоди",
  "Сімейка",
  "Самостійсть",
  "Гігієна"
];
const OTHER_CATEGORY = "Інше";

function TasksTab() {
  const [tasks, setTasks] = useState([]);
  const [children, setChildren] = useState([]);
  const [idToNameMap, setIdToNameMap] = useState({});
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    icon: "",
    points: 0,
    category: "",
    repeat: "once",
    assignedTo: []
  });
  const [editingTask, setEditingTask] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const tasksSnap = await getDocs(collection(db, "tasks"));
    const fetchedTasks = tasksSnap.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .sort((a, b) => {
        if ((a.active === false) !== (b.active === false)) {
          return a.active === false ? -1 : 1;
        }
        return (a.title || "").localeCompare(b.title || "", "uk");
      });
    setTasks(fetchedTasks);

    const childrenSnap = await getDocs(
      query(collection(db, "users"), where("type", "==", "child"))
    );
    const fetchedChildren = childrenSnap.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    setChildren(fetchedChildren);

    const map = {};
    fetchedChildren.forEach(child => {
      map[child.id] = child.name;
    });
    setIdToNameMap(map);
  };

  const handleAssignChange = (id, task, setTask) => {
    const assigned = task.assignedTo.includes(id)
      ? task.assignedTo.filter(n => n !== id)
      : [...task.assignedTo, id];
    setTask({ ...task, assignedTo: assigned });
  };

  const handleSaveTask = async (task, reset = true) => {
    if (!task.title || !task.points || task.assignedTo.length === 0) return;

    const preparedTask = {
      ...task,
      points: Number(task.points),
      category: task.category || "",
      active: true,
    };

    if (task.id) {
      const ref = doc(db, "tasks", task.id);
      await updateDoc(ref, preparedTask);
    } else {
      await addDoc(collection(db, "tasks"), preparedTask);
    }

    if (reset) {
      setNewTask({
        title: "",
        description: "",
        icon: "",
        points: 0,
        category: "",
        repeat: "once",
        assignedTo: []
      });
    }

    setEditingTask(null);
    setShowAddModal(false);
    await loadData();
  };

  const handleDeleteTask = async (id) => {
    if (window.confirm("Точно видалити завдання?")) {
      await deleteDoc(doc(db, "tasks", id));
      loadData();
    }
  };

  const repeatUkr = {
    once: "Одноразове",
    daily: "Щодня",
    weekly: "Щотижня"
  };

  const filteredTasks = tasks.filter(task =>
    task.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const groupedTasks = TASK_CATEGORIES.map(category => ({
    category,
    tasks: filteredTasks.filter(task => task.category === category)
  })).filter(group => group.tasks.length > 0);

  const otherTasks = filteredTasks.filter(
    task => !TASK_CATEGORIES.includes(task.category)
  );
  if (otherTasks.length > 0) {
    groupedTasks.push({
      category: OTHER_CATEGORY,
      tasks: otherTasks
    });
  }

  return (
    <div className="tasks-tab">
      <div className="task-header-row">
        <h2>Перелік завдань</h2>
        <div className="task-controls">
          <input
            type="text"
            placeholder="Пошук завдань..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="add-task-btn" onClick={() => setShowAddModal(true)}>Додати завдання</button>
        </div>
      </div>

      <div className="task-category-list">
        {groupedTasks.map(group => (
          <section key={group.category} className="task-category-section">
            <h3>{group.category}</h3>
            <div className="task-list">
              {group.tasks.map(task => (
                <div key={task.id} className="task-card">
                  <div className="task-header">
                    <div className="task-title-icon">
                      <Emoji label="Іконка завдання" symbol={task.icon || "📝"} /> {task.title}
                    </div>
                    <div className="task-actions-inline">
                      <button onClick={() => setEditingTask(task)}><Emoji label="Редагувати" symbol="✏️" /></button>
                      <button className="delete-btn" onClick={() => handleDeleteTask(task.id)}><Emoji label="Видалити" symbol="🗑️" /></button>
                    </div>
                  </div>
                  {task.active === false && <span className="inactive-label">Неактивне</span>}
                  <p>{task.description}</p>
                  <p><Emoji label="Кубок" symbol="🏆" /> {task.points} балів</p>
                  <p>Повторення: {repeatUkr[task.repeat]}</p>
                  <p>
                    Призначено: {task.assignedTo.map(id => idToNameMap[id] || "?").join(", ")}
                  </p>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>

      {(showAddModal || editingTask) && (
        <div className="modal-backdrop">
          <div className="modal-content">
            <h2>{editingTask ? "Редагування завдання" : "Нове завдання"}</h2>
            <TaskForm
              task={editingTask || newTask}
              setTask={editingTask ? setEditingTask : setNewTask}
              children={children}
              categories={TASK_CATEGORIES}
              onSave={handleSaveTask}
              handleAssignChange={handleAssignChange}
              onCancel={() => {
                setEditingTask(null);
                setShowAddModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function TaskForm({ task, setTask, children, categories, onSave, handleAssignChange, onCancel }) {
  return (
    <div className="task-form">
      <div className="form-row"><label>Назва:</label>
        <input value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} />
      </div>

      <div className="form-row"><label>Опис:</label>
        <textarea value={task.description} onChange={(e) => setTask({ ...task, description: e.target.value })} />
      </div>

      <div className="form-row">
        <label>Іконка:</label>
        <div className="icon-preview"><Emoji label="Іконка" symbol={task.icon || "📝"} /></div>
      </div>

      <EmojiPicker
        onEmojiClick={(emojiData) => setTask({ ...task, icon: emojiData.emoji })}
        height={300}
        width="100%"
      />

      <div className="form-row"><label>Бали:</label>
        <input type="number" value={task.points} onChange={(e) => setTask({ ...task, points: e.target.value })} />
      </div>

      <div className="form-row"><label>Категорія:</label>
        <select value={task.category || ""} onChange={(e) => setTask({ ...task, category: e.target.value })}>
          <option value="">Без категорії</option>
          {categories.map(category => (
            <option key={category} value={category}>{category}</option>
          ))}
        </select>
      </div>

      <div className="form-row"><label>Повторення:</label>
        <select value={task.repeat} onChange={(e) => setTask({ ...task, repeat: e.target.value })}>
          <option value="once">Одноразове</option>
          <option value="daily">Щодня</option>
          <option value="weekly">Щотижня</option>
        </select>
      </div>

      <div className="form-row full-width">
        <label>Призначено:</label>
        <div className="assign-list">
          {children.map(child => (
            <label key={child.id}>
              <input
                type="checkbox"
                checked={task.assignedTo.includes(child.id)}
                onChange={() => handleAssignChange(child.id, task, setTask)}
              />
              {child.name}
            </label>
          ))}
        </div>
      </div>

      <div className="modal-actions-row">
        <button className="save-task-btn" onClick={() => onSave(task)}>Зберегти</button>
        <button className="cancel-btn" onClick={onCancel}>Скасувати</button>
      </div>
    </div>
  );
}

export default TasksTab;
